import { useCallback, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useReportCRUD } from "~/hooks/useReportCRUD";
import { handleQueryError } from "~/helpers/errorHandling.helper";
import { useReportDetailStore } from "~/store/details/store";
import { REPORT_DETAIL_STORE_INSTANCE, UpdateFnPromise } from "~/store/details";
import { usePasswordCheck } from "~/hooks/usePasswordCheck";
import { useSessionGuard } from "~/hooks/useSessionGuard";
import { CHECK_SESSION_INTERVAL } from "~/helpers/constants";
import { setGlobalForceSave } from "~/store/details/autoSaveGlobal";

export function useReportActionsWithAutoSave(refetchReport?: () => Promise<any>) {
  const { checkSessionBeforeAction } = useSessionGuard({ interval: CHECK_SESSION_INTERVAL[1] });
  const { withPasswordCheck } = usePasswordCheck();
  const actions = useReportDetailStore((state) => state.actions);
  const { addNewReportMutation,
    setReportDetailsToStale,
    resetReportListQuery
  } = useReportCRUD();

  const executeSave = useCallback(() => {
    const state = useReportDetailStore.getState();

    if (!state.autoSave.hasPendingChanges || state.autoSave.isPending) {
      return;
    }

    const { sections, metadata } = state.data;
    const reportId = metadata?.user_reports_id as string;

    const payload = {
      ...metadata,
      data: { [metadata?.report_type as string]: sections },
    };

    console.log("[useReportActionsWithAutoSave] Executando save com estado final:", payload);

    useReportDetailStore.setState((state) => ({
      ...state,
      autoSave: {
        ...state.autoSave,
        isPending: true,
        hasPendingChanges: false,
        mutation: addNewReportMutation,
      }
    }));

    addNewReportMutation.mutate(payload, {
      onSuccess: () => {
        useReportDetailStore.setState((state) => ({
          ...state,
          autoSave: {
            ...state.autoSave,
            isPending: false,
          }
        }));

        toast.success("Alterações salvas com sucesso!");
        withPasswordCheck(() => {
          if (reportId) {
            setReportDetailsToStale(reportId)
            resetReportListQuery()
          };
        });
      },
      onError: (error) => {
        useReportDetailStore.setState((state) => ({
          ...state,
          autoSave: {
            ...state.autoSave,
            isPending: false,
            hasPendingChanges: false,
          }
        }));

        handleQueryError(error, {
          serverErrorMessage: "Falha ao salvar, tente novamente mais tarde.",
          title: "Erro ao salvar",
          onError: () => {
            if (reportId) {
              actions.forceReload();
            }
          }
        });
      },
    });
  }, [addNewReportMutation, actions, withPasswordCheck, setReportDetailsToStale, resetReportListQuery]);

  const cancelChanges = useCallback(async () => {
    actions.cancelAutoSave();

    if (refetchReport) {
      const currentState = useReportDetailStore.getState();
      const currentSections = currentState.data.sections;
      const openSectionIds = currentState.ui.openSections;

      const result = await refetchReport();

      if (result.data) {
        const { data: dataMap } = result.data;
        const typeKey = Object.keys(dataMap)[0];
        const foundSections: any[] = dataMap?.[typeKey] || [];

        // Preserva interface_ids para manter accordions abertos
        const sectionsWithPreservedIds = foundSections.map((section) => {
          const existingSection = currentSections.find(
            (s) => s.title === section.title && s.subsection === section.subsection
          );
          return {
            ...section,
            interface_id: existingSection?.interface_id || crypto.randomUUID()
          };
        });

        REPORT_DETAIL_STORE_INSTANCE.events.setReportSections(sectionsWithPreservedIds);
        REPORT_DETAIL_STORE_INSTANCE.events.setOpenSections(openSectionIds);
      }

      toast.info("Alterações descartadas");
    }
  }, [actions, refetchReport]);

  useEffect(() => {
    setGlobalForceSave(executeSave);
    return () => {
      setGlobalForceSave(null);
    };
  }, [executeSave]);

  const updateSectionEntries = useCallback((
    sectionTitle: string,
    updaterFn: UpdateFnPromise,
    testEntryDeletedFn: (entry: any) => boolean,
    testSectionDeletedFn: (section: any) => boolean,
    calculateDataCountFn?: (section: any) => number,
    includeSubsections?: boolean,
    crossSectionUpdate?: { matchingProp: string; updaterFn: UpdateFnPromise }
  ) => {
    checkSessionBeforeAction();

    REPORT_DETAIL_STORE_INSTANCE.events.updateSectionEntries(
      sectionTitle,
      updaterFn,
      testEntryDeletedFn,
      testSectionDeletedFn,
      calculateDataCountFn,
      includeSubsections,
      crossSectionUpdate
    );

    REPORT_DETAIL_STORE_INSTANCE.events.scheduleAutoSave();
  }, [checkSessionBeforeAction]);

  const updateSubsectionWithMainSection = useCallback((
    sectionTitle: string,
    subsectionName: string,
    matchingProp: string,
    updaterFn: UpdateFnPromise,
    testEntryDeletedFn: (entry: any) => boolean,
    testSectionDeletedFn: (section: any) => boolean,
    calculateDataCountFn?: (section: any) => number
  ) => {
    checkSessionBeforeAction();

    REPORT_DETAIL_STORE_INSTANCE.events.updateSubsectionWithMainSection(
      sectionTitle,
      subsectionName,
      matchingProp,
      updaterFn,
      testEntryDeletedFn,
      testSectionDeletedFn,
      calculateDataCountFn
    );

    REPORT_DETAIL_STORE_INSTANCE.events.scheduleAutoSave();
  }, [checkSessionBeforeAction]);

  return useMemo(() => ({
    ...actions,
    updateSectionEntries,
    updateSubsectionWithMainSection,
    forceSave: executeSave,
    cancelChanges,
  }), [actions, updateSectionEntries, updateSubsectionWithMainSection, executeSave, cancelChanges]);
}

